import { useEffect, useRef, useState } from 'react';
import metadataService from '../api/metadataService';

interface UseCollectionFreshnessOptions {
  interval?: number;
  enabled?: boolean;
}

/**
 * Polls the backend metadata endpoint to detect when collection data has changed.
 * Returns the latest timestamps per collection. Polling only runs when enabled
 * and stops when the component unmounts.
 */
export function useCollectionFreshness(
  collections: string[],
  options: UseCollectionFreshnessOptions = {}
): Record<string, string | null> {
  const { interval = 30_000, enabled = true } = options;
  const [timestamps, setTimestamps] = useState<Record<string, string | null>>({});
  const collectionsKey = collections.join(',');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    if (!enabled || collections.length === 0) {
      return;
    }

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const poll = async () => {
      try {
        const result = await metadataService.getLastUpdated(collections);
        if (mountedRef.current) {
          setTimestamps(prev => {
            const hasChange = collections.some(c => result[c] !== prev[c]);
            return hasChange ? result : prev;
          });
        }
      } catch {
        // Silently ignore polling errors — non-critical
      }
    };

    // Initial poll
    poll();

    // Start interval
    intervalId = setInterval(poll, interval);

    return () => {
      mountedRef.current = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [collectionsKey, interval, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return timestamps;
}
